import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  addLogisticsCompany, 
  updateLogisticsCompany, 
  deleteLogisticsCompany, 
  pageLogisticsCompanies,
  getLogisticsCompanyDetail
} from '../../api/logisticsCompany';
import { message } from 'antd';

// 分页查询物流公司列表的异步 thunk
export const fetchLogisticsCompanies = createAsyncThunk(
  'logisticsCompany/fetchList',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await pageLogisticsCompanies(params);
      return response.data;
    } catch (error) {
      message.error('获取物流公司列表失败');
      return rejectWithValue(error.response?.data);
    }
  }
);

// 获取物流公司详情的异步 thunk
export const fetchLogisticsCompanyDetail = createAsyncThunk(
  'logisticsCompany/fetchDetail',
  async (id, { rejectWithValue }) => {
    try {
      const response = await getLogisticsCompanyDetail(id);
      return response.data;
    } catch (error) {
      message.error('获取物流公司详情失败');
      return rejectWithValue(error.response?.data);
    }
  }
);

// 添加物流公司的异步 thunk
export const createLogisticsCompany = createAsyncThunk(
  'logisticsCompany/create',
  async (data, { dispatch, rejectWithValue }) => {
    try {
      const response = await addLogisticsCompany(data);

      if (response.code === 200) {
        message.success('添加物流公司成功');
        // 重新获取列表
        dispatch(fetchLogisticsCompanies());
        return response.data;
      } else {
        message.error(response.message || '添加失败');
        return rejectWithValue(response);
      }
    } catch (error) {
      message.error('添加物流公司请求失败');
      return rejectWithValue(error.response?.data);
    }
  }
);

// 更新物流公司的异步 thunk
export const updateLogisticsCompanyAction = createAsyncThunk(
  'logisticsCompany/update',
  async (data, { dispatch, rejectWithValue }) => {
    try {
      const response = await updateLogisticsCompany(data);

      if (response.code === 200) {
        message.success('更新物流公司成功');
        // 重新获取列表
        dispatch(fetchLogisticsCompanies());
        return response.data;
      } else {
        message.error(response.message || '更新失败');
        return rejectWithValue(response);
      }
    } catch (error) {
      message.error('更新物流公司请求失败');
      return rejectWithValue(error.response?.data);
    }
  }
);

// 删除物流公司的异步 thunk
export const deleteLogisticsCompanyAction = createAsyncThunk(
  'logisticsCompany/delete',
  async (id, { dispatch, rejectWithValue }) => {
    try {
      const response = await deleteLogisticsCompany(id);

      if (response.code === 200) {
        message.success('删除物流公司成功');
        // 重新获取列表
        dispatch(fetchLogisticsCompanies());
        return response.data;
      } else {
        message.error(response.message || '删除失败');
        return rejectWithValue(response);
      }
    } catch (error) {
      message.error('删除物流公司请求失败');
      return rejectWithValue(error.response?.data);
    }
  }
);

const logisticsCompanySlice = createSlice({
  name: 'logisticsCompany',
  initialState: {
    companies: [],
    total: 0,
    page: 1,
    pageSize: 10,
    loading: false,
    error: null,
    currentCompany: null
  },
  reducers: {
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
    },
    setCurrentCompany: (state, action) => {
      state.currentCompany = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // 获取列表
      .addCase(fetchLogisticsCompanies.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLogisticsCompanies.fulfilled, (state, action) => {
        state.loading = false;
        state.companies = action.payload.records;
        state.total = action.payload.total;
        state.page = action.payload.current;
        state.pageSize = action.payload.size;
      })
      .addCase(fetchLogisticsCompanies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // 获取详情
      .addCase(fetchLogisticsCompanyDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLogisticsCompanyDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.currentCompany = action.payload;
      })
      .addCase(fetchLogisticsCompanyDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // 新增
      .addCase(createLogisticsCompany.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createLogisticsCompany.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createLogisticsCompany.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // 更新
      .addCase(updateLogisticsCompanyAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateLogisticsCompanyAction.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateLogisticsCompanyAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // 删除
      .addCase(deleteLogisticsCompanyAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteLogisticsCompanyAction.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteLogisticsCompanyAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const {
  setPage,
  setPageSize,
  setCurrentCompany
} = logisticsCompanySlice.actions;

export default logisticsCompanySlice.reducer;
